import LocalizationUtils from '~/localization/LocalizationUtils';

export const emailVerificationURL = () =>
    window.location.origin.concat(LocalizationUtils.buildURLWithLocale('/verify'));

export const recoverPasswordURL = () =>
    window.location.origin.concat(LocalizationUtils.buildURLWithLocale('/recover-password'));

export const getDomainName = () => {
    let domain = window.location.origin;
    domain = domain.replace('http://', '');
    domain = domain.replace('https://', '');
    return domain;
};

export const getMainDomainUrl = () => {
    let domain = window.location.hostname;
    if (domain == '127.0.0.1') {
        return domain;
    }

    // Remove subdomain if exists
    const firstDotIndex = domain.indexOf('.');
    if (firstDotIndex !== -1) {
        const secondDotIndex = domain.indexOf('.', firstDotIndex + 1);

        // if secondDotIndex is not -1, the domain then contains a subdomain
        if (secondDotIndex !== -1) {
            domain = domain.substring(firstDotIndex + 1);
        }
    }

    return domain;
};

// getCookieDomainUrl returns the domain name for cookies using the main domain url and a '.' prefix.
export const getCookieDomainUrl = () => {
    const domain = getMainDomainUrl();
    if (domain === 'localhost') {
        return domain;
    }
    return `.${domain}`;
};

type StringNumberBoolean = string | number | boolean;
type QueryObject = Record<string, StringNumberBoolean | StringNumberBoolean[]>;
export const objectToQueryString = (queryObject: QueryObject) => {
    return '?'.concat(
        Object.keys(queryObject)
            .map(key => {
                if (Array.isArray(queryObject[key])) {
                    return (queryObject[key] as StringNumberBoolean[])
                        .map(
                            (value: StringNumberBoolean) =>
                                `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
                        )
                        .join('&');
                }
                return `${encodeURIComponent(key)}=${encodeURIComponent(
                    queryObject[key] as string
                )}`;
            })
            .join('&')
    );
};

// https://stackoverflow.com/questions/8486099/how-do-i-parse-a-url-query-parameters-in-javascript
export const getJsonFromUrl = (url: string) => {
    if (!url) url = location.href;
    const question = url.indexOf('?');
    let hash = url.indexOf('#');
    if (hash === -1 && question === -1) return {};
    if (hash === -1) hash = url.length;
    const query =
        question === -1 || hash === question + 1
            ? url.substring(hash)
            : url.substring(question + 1, hash);
    const result: Record<string, string | string[]> = {};
    query.split('&').forEach(function (part) {
        if (!part) return;
        part = part.split('+').join(' '); // replace every + with space, regexp-free version
        const eq = part.indexOf('=');
        let key = eq > -1 ? part.substr(0, eq) : part;
        const val = eq > -1 ? decodeURIComponent(part.substr(eq + 1)) : '';
        const from = key.indexOf('[');
        if (from === -1) result[decodeURIComponent(key)] = val;
        else {
            const to = key.indexOf(']', from);
            const index = Number(decodeURIComponent(key.substring(from + 1, to)));
            key = decodeURIComponent(key.substring(0, from));
            if (!result[key]) result[key] = [];
            if (!index) (result[key] as string[]).push(val);
            else (result[key] as string[])[index] = val;
        }
    });
    return result;
};

export const urlValidator = (s: string) =>
    s.match(
        /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g
    ) !== null;

// Extracts an number id, such as the job ID, from the URL with the format of :id<number>-:title<string>
// Even if id should be a number, it is returned as a string as casting large number to a Javascript number
// can cause precision loss.
export const numberIdFromParam = (param: string): string | null => {
    const urlParts = param.split('-', 1);
    const jobId = urlParts[0];

    // Parse jobId to number
    if (isNaN(Number(jobId))) {
        return null;
    }
    return jobId;
};

export const uuidFromURLParam = (param: string): string | null => {
    const UUID_LENGTH = 36;
    const uuid = param.slice(0, UUID_LENGTH);

    // Parse jobId to number
    if (uuid.length !== UUID_LENGTH) {
        return null;
    }

    // Validate UUID
    const uuidRegex = new RegExp(
        '^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$'
    );
    if (!uuidRegex.test(uuid)) {
        return null;
    }

    return uuid;
};

export const buildIDParam = (id: string, title?: string) => {
    if (title) {
        return `${id}-${formatTitleToURL(title)}`;
    }
    return id;
};

export const formatTitleToURL = (title: string) => {
    return title
        .toLowerCase()
        .slice(0, 100)
        .replace(/ /g, '-')
        .replace(/-+/g, '-')
        .replace(/[^a-zA-Z0-9-]/g, '');
};
